import React, { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Button } from "components/atoms/Button";
import { ButtonType } from "components/atoms/Button/types";
import { Modal } from "components/atoms/Modal";
import { colourScheme, hexToRgba } from "utils/styling/colours";
import { Text } from "utils/styling/typography.styled";
import { loadScript } from "utils/utils";
import { BackgroundImageWrapper, ButtonContainer, CustomDialog, CustomDialogActions, CustomDialogContent, CustomDialogTitle, GoldCost, ModalContainer, StyledCanvas, Toolbar, ToolButton } from "./CanvasModal.styled";
enum Tool {
  DRAW,
  ERASE,
  CLEAR,
}
interface CanvasModalProps {
  isOpen: boolean;
  imageDto: any;
  onClose: () => void;
  onMaskSubmit: (maskData: any) => void;
}
const CanvasModal: React.FC<CanvasModalProps> = ({
  isOpen,
  imageDto,
  onClose,
  onMaskSubmit
}) => {
  //@ts-ignore - Fabric Exists as a Custom Build
  const canvasRef = useRef<fabric.Canvas | null>(null);
  const brushSize = 40;
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [originalDimensions, setOriginalDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [currentTool, setCurrentTool] = useState<Tool>(Tool.DRAW);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  // Configuration for undo/redo states
  const config = useRef({
    canvasState: [] as string[],
    currentStateIndex: -1,
    undoStatus: false,
    redoStatus: false,
    undoFinishedStatus: true,
    redoFinishedStatus: true
  }).current;

  // Function to update the canvas state
  const updateCanvasState = useCallback(() => {
    if (!config.undoStatus && !config.redoStatus) {
      const jsonData = canvasRef.current!.toJSON();
      const canvasAsJson = JSON.stringify(jsonData);
      if (config.currentStateIndex < config.canvasState.length - 1) {
        const indexToBeInserted = config.currentStateIndex + 1;
        config.canvasState[indexToBeInserted] = canvasAsJson;
        config.canvasState = config.canvasState.splice(0, indexToBeInserted + 1);
      } else {
        config.canvasState.push(canvasAsJson);
      }
      config.currentStateIndex = config.canvasState.length - 1;
      setCanUndo(config.currentStateIndex > 0);
      setCanRedo(false);
    }
  }, [config]);
  const handleUndo = () => {
    if (config.undoFinishedStatus) {
      if (config.currentStateIndex > 0) {
        config.undoStatus = true;
        config.undoFinishedStatus = false;
        canvasRef.current!.loadFromJSON(config.canvasState[config.currentStateIndex - 1], () => {
          canvasRef.current!.renderAll();
          config.currentStateIndex -= 1;
          config.undoStatus = false;
          setCanUndo(config.currentStateIndex > 0);
          setCanRedo(true);
          config.undoFinishedStatus = true;
        });
      } else {
        setCanUndo(false);
      }
    }
  };
  const handleRedo = () => {
    if (config.redoFinishedStatus) {
      if (config.currentStateIndex < config.canvasState.length - 1) {
        config.redoStatus = true;
        config.redoFinishedStatus = false;
        canvasRef.current!.loadFromJSON(config.canvasState[config.currentStateIndex + 1], () => {
          canvasRef.current!.renderAll();
          config.currentStateIndex += 1;
          config.redoStatus = false;
          setCanUndo(true);
          setCanRedo(config.currentStateIndex < config.canvasState.length - 1);
          config.redoFinishedStatus = true;
        });
      } else {
        setCanRedo(false);
      }
    }
  };
  const calculateImageDimensions = useCallback(() => {
    const img = new window.Image();
    img.src = imageDto.image;
    img.onload = () => {
      const originalWidth = img.width;
      const originalHeight = img.height;
      setOriginalDimensions({
        width: originalWidth,
        height: originalHeight
      });
      const maxWidth = window.innerWidth * 0.8;
      const maxHeight = window.innerHeight * 0.8;
      const aspectRatio = img.width / img.height;
      let width = img.width;
      let height = img.height;
      if (width > maxWidth) {
        width = maxWidth;
        height = width / aspectRatio;
      }
      if (height > maxHeight) {
        height = maxHeight;
        width = height * aspectRatio;
      }
      setImageDimensions({
        width,
        height
      });
    };
  }, [imageDto?.image]);
  useEffect(() => {
    if (isOpen) {
      calculateImageDimensions();
    }
  }, [calculateImageDimensions, imageDto?.image, isOpen]);
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        calculateImageDimensions();
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [calculateImageDimensions, isOpen]);
  useEffect(() => {
    let isMounted = true;
    let fabric: any;
    if (imageDimensions && isOpen) {
      (async () => {
        await loadScript("/fabric.min.js");
        if (!isMounted) return;

        // Now that the script is loaded, fabric should be available globally
        fabric = (window as any).fabric;
        if (!fabric) {
          throw new Error("Fabric.js not loaded");
        }
        const canvas = new fabric.Canvas("fabricCanvas", {
          isDrawingMode: true,
          backgroundColor: "transparent"
        });
        canvas.setWidth(imageDimensions.width);
        canvas.setHeight(imageDimensions.height);
        canvasRef.current = canvas;
        handleSetDrawMode(); // Set the default tool to draw

        // Save initial state
        updateCanvasState();
        canvas.on("object:modified", updateCanvasState);
        canvas.on("object:added", updateCanvasState);
        canvas.on("path:created", updateCanvasState);
      })();
    }
    return () => {
      isMounted = false;
      if (canvasRef.current) {
        canvasRef.current.dispose();
        canvasRef.current = null;
      }
    };
  }, [imageDimensions, isOpen, updateCanvasState]);
  const handleSetDrawMode = () => {
    setCurrentTool(Tool.DRAW);
    if (canvasRef.current) {
      canvasRef.current.isDrawingMode = true;
      canvasRef.current.freeDrawingBrush = new fabric.PencilBrush(canvasRef.current);
      canvasRef.current.freeDrawingBrush.width = brushSize;
      canvasRef.current.freeDrawingBrush.color = hexToRgba(`${colourScheme.successDark}`, 0.75);
    }
  };
  const handleSetEraseMode = () => {
    setCurrentTool(Tool.ERASE);
    if (canvasRef.current) {
      canvasRef.current.isDrawingMode = true;
      const eraserBrush = new fabric.EraserBrush(canvasRef.current);
      eraserBrush.width = brushSize;
      canvasRef.current.freeDrawingBrush = eraserBrush;
    }
  };
  const handleClearCanvas = () => {
    if (canvasRef.current) {
      canvasRef.current.clear();
      canvasRef.current.renderAll();
      config.canvasState = [];
      config.currentStateIndex = -1;
      setCanUndo(false);
      setCanRedo(false);
    }
  };
  const handleSubmit = () => {
    if (canvasRef.current && imageDimensions && originalDimensions) {
      const originalWidth = originalDimensions.width;
      const originalHeight = originalDimensions.height;
      // Calculate the scaling factors
      const scaleX = originalWidth / imageDimensions.width;
      const scaleY = originalHeight / imageDimensions.height;

      // Temporarily scale the canvas to the original image size
      canvasRef.current.setWidth(originalWidth);
      canvasRef.current.setHeight(originalHeight);
      if (scaleX !== scaleY) {
        canvasRef.current.getObjects().forEach((obj: {
          scaleX: number;
          scaleY: number;
          left: number;
          top: number;
          setCoords: () => void;
        }) => {
          obj.scaleX *= scaleX;
          obj.scaleY *= scaleY;
          obj.left *= scaleX;
          obj.top *= scaleY;
          obj.setCoords(); // Update object coordinates
        });
      } else {
        canvasRef.current.setZoom(scaleX); // Assuming uniform scaling (same for X and Y)
      }
      // Convert all objects to black with full opacity before export
      canvasRef.current.getObjects().forEach((obj: {
        type: string;
        set: (arg0: {
          stroke: string;
          fill: string;
          opacity: number;
        }) => void;
      }) => {
        if (obj.type === "path" || obj.type === "rect") {
          obj.set({
            stroke: "white",
            fill: "white",
            opacity: 1 // Ensure full opacity
          });
        }
      });

      // Render the changes to the canvas
      canvasRef.current.renderAll();

      // Export the mask as a PNG image with full opacity
      const maskDataUrl = canvasRef.current.toDataURL({
        format: "png",
        multiplier: 1
      });

      // Pass the mask data URL to the backend or inpainting model
      onMaskSubmit(maskDataUrl);

      // Reset the canvas to its original dimensions and zoom
      canvasRef.current.setWidth(imageDimensions.width);
      canvasRef.current.setHeight(imageDimensions.height);
      canvasRef.current.setZoom(1); // Reset zoom to original state
      canvasRef.current.renderAll();
      handleClose();
    }
  };
  const handleClose = () => {
    handleClearCanvas();
    if (canvasRef.current) {
      canvasRef.current.dispose();
      canvasRef.current = null;
    }
    setImageDimensions(null);
    onClose();
  };
  return <Modal open={isOpen} handleBackdropClick={onClose} disableBackdropClick data-sentry-element="Modal" data-sentry-component="CanvasModal" data-sentry-source-file="index.tsx">
            <ModalContainer data-sentry-element="ModalContainer" data-sentry-source-file="index.tsx">
                <Toolbar data-sentry-element="Toolbar" data-sentry-source-file="index.tsx">
                    <ToolButton onClick={handleSetDrawMode} isSelected={currentTool === Tool.DRAW} data-sentry-element="ToolButton" data-sentry-source-file="index.tsx">
                        ✏️ Draw
                    </ToolButton>
                    <ToolButton onClick={handleSetEraseMode} isSelected={currentTool === Tool.ERASE} data-sentry-element="ToolButton" data-sentry-source-file="index.tsx">
                        🧹 Erase
                    </ToolButton>
                    <ToolButton onClick={handleClearCanvas} isSelected={currentTool === Tool.CLEAR} data-sentry-element="ToolButton" data-sentry-source-file="index.tsx">
                        🗑️ Clear
                    </ToolButton>
                    <ToolButton onClick={handleUndo} disabled={!canUndo} data-sentry-element="ToolButton" data-sentry-source-file="index.tsx">
                        ↩️ Undo
                    </ToolButton>
                    <ToolButton onClick={handleRedo} disabled={!canRedo} data-sentry-element="ToolButton" data-sentry-source-file="index.tsx">
                        ↪️ Redo
                    </ToolButton>
                    <ToolButton onClick={() => setIsInfoOpen(true)} data-sentry-element="ToolButton" data-sentry-source-file="index.tsx">
                        ❓ Help
                    </ToolButton>
                </Toolbar>
                {imageDimensions && <BackgroundImageWrapper width={imageDimensions?.width} height={imageDimensions?.height}>
                        <Image src={imageDto.image} alt="Background" width={imageDimensions?.width} height={imageDimensions?.height} layout="responsive" style={{
          zIndex: 1,
          position: "absolute",
          top: 0,
          left: 0
        }} />
                        <StyledCanvas id="fabricCanvas" width={imageDimensions?.width} height={imageDimensions?.height} />
                    </BackgroundImageWrapper>}
                <GoldCost data-sentry-element="GoldCost" data-sentry-source-file="index.tsx">Gold Cost: 1 Gold</GoldCost>
                <ButtonContainer data-sentry-element="ButtonContainer" data-sentry-source-file="index.tsx">
                    <Button variant={ButtonType.SECONDARY} onClick={handleSubmit} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        Submit
                    </Button>
                    <Button variant={ButtonType.PRIMARY} onClick={handleClose} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        Cancel
                    </Button>
                </ButtonContainer>
            </ModalContainer>
            <CustomDialog open={isInfoOpen} onClose={() => setIsInfoOpen(false)} data-sentry-element="CustomDialog" data-sentry-source-file="index.tsx">
                <CustomDialogTitle data-sentry-element="CustomDialogTitle" data-sentry-source-file="index.tsx">How to Use Spot Edit</CustomDialogTitle>
                <CustomDialogContent dividers data-sentry-element="CustomDialogContent" data-sentry-source-file="index.tsx">
                    <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        The Spot Edit tool allows you to perform *inpainting*, a
                        process used to edit and replace specific areas of an
                        image.
                    </Text>
                    <br />
                    <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        Begin by selecting the area you want to modify using the
                        available tools. On the next screen, you will specify
                        the changes you want to make to that area.
                    </Text>
                    <br />
                    <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">The tools include:</Text>
                    <br />
                    <ul>
                        <li>
                            <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">
                                ✏️ <strong>Draw:</strong> Add marks to the
                                selected area.
                            </Text>
                        </li>
                        <li>
                            <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">
                                🧹 <strong>Erase:</strong> Remove marks from the
                                selected area.
                            </Text>
                        </li>
                        <li>
                            <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">
                                🗑️ <strong>Clear:</strong> Reset the entire
                                canvas.
                            </Text>
                        </li>
                        <li>
                            <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">
                                ↩️ <strong>Undo:</strong> Revert the last
                                action.
                            </Text>
                        </li>
                        <li>
                            <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">
                                ↪️ <strong>Redo:</strong> Reapply the last
                                undone action.
                            </Text>
                        </li>
                    </ul>
                    <br />
                    <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        This tool gives you the flexibility to manipulate and
                        transform selected sections of your image with ease.
                    </Text>
                </CustomDialogContent>
                <CustomDialogActions data-sentry-element="CustomDialogActions" data-sentry-source-file="index.tsx">
                    <Button onClick={() => setIsInfoOpen(false)} variant={ButtonType.PRIMARY} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        Got it!
                    </Button>
                </CustomDialogActions>
            </CustomDialog>
        </Modal>;
};
export default CanvasModal;